// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Styled, Space, DataLoader} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useUrlFilters, useQuery, useResponsive, ResponsiveType} from '@supermove/hooks';
import {UserModel} from '@supermove/models';

// App
import Line from '@shared/design/components/Line';
import Tabs from '@shared/design/components/Tabs';
import FullWidthTabs from '@shared/design/components/Tabs/FullWidthTabs';
import PageLoadingIndicator from '@shared/modules/App/components/PageLoadingIndicator';
import {ProjectStatusType} from '@shared/modules/Project/enums/ProjectStatus';
import SettingsProjectStages from '@shared/modules/Settings/components/SettingsProjectStages';

const TabsContainer = Styled.View`
`;

const HeaderFiltersContainer = Styled.View<{responsive: ResponsiveType}>`
  padding-horizontal: ${({responsive}) => (responsive.desktop ? '24px' : '8px')};
`;

const getIsArchivedTabs = ({activeStagesCount, archivedStagesCount}: any) => [
  {
    key: 'ACTIVE',
    label: 'Active',
    value: false,
    count: activeStagesCount,
  },
  {
    key: 'ARCHIVED',
    label: 'Archived',
    value: true,
    count: archivedStagesCount,
  },
];

interface TabHeaderProps {
  isArchived: boolean;
  setIsArchived: (value: boolean) => void;
  activeStagesCount: number;
  archivedStagesCount: number;
}

const TabHeader = ({
  isArchived,
  setIsArchived,
  activeStagesCount,
  archivedStagesCount,
}: TabHeaderProps) => {
  const isArchivedTabs = getIsArchivedTabs({
    activeStagesCount,
    archivedStagesCount,
  });
  const responsive = useResponsive();
  const activeTabIndex = _.findIndex(
    isArchivedTabs,
    (definition) => definition.value === isArchived,
  );

  return (
    <TabsContainer>
      {responsive.desktop ? (
        <Tabs
          tabs={isArchivedTabs}
          handlePressTab={({value}) => {
            setIsArchived(value);
          }}
          activeTabIndex={activeTabIndex}
        />
      ) : (
        <FullWidthTabs
          tabs={isArchivedTabs}
          handlePressTab={({value}) => {
            setIsArchived(value);
          }}
          activeTabIndex={activeTabIndex}
        />
      )}
    </TabsContainer>
  );
};

const SettingsSalesStagesPageContent = ({
  handleAdd,
  handleView,
}: {
  isArchived: boolean;
  setIsArchived: (value: boolean) => void;
  handleAdd: (projectStatus: ProjectStatusType) => void;
  handleView: (stageUuid: string) => void;
}) => {
  const responsive = useResponsive();
  const {loading, data, refetch} = useQuery<{viewer: UserModel}>(
    SettingsSalesStagesPageContent.query,
    {
      fetchPolicy: 'cache-and-network',
    },
  );

  const urlFilters = useUrlFilters({
    getRoute: () => '/settings/projects/stages',
    filterKeys: ['isArchived'],
  });
  const isArchived = urlFilters.get('isArchived') === 'true';
  const setIsArchived = (value: boolean) => {
    urlFilters.handleUpdate({isArchived: value});
  };

  return (
    <DataLoader loading={loading} data={data} as={PageLoadingIndicator}>
      {({loadedData}) => {
        const {salesStages, archivedSalesStages} =
          loadedData.viewer.viewingOrganization.companySettings;
        return (
          <React.Fragment>
            <HeaderFiltersContainer responsive={responsive}>
              <TabHeader
                isArchived={isArchived}
                setIsArchived={setIsArchived}
                activeStagesCount={salesStages.length}
                archivedStagesCount={archivedSalesStages.length}
              />
              <Line />
            </HeaderFiltersContainer>
            <Space height={16} />
            <SettingsProjectStages
              stages={isArchived ? archivedSalesStages : salesStages}
              handleAdd={handleAdd}
              handleView={handleView}
              refetch={refetch}
              scrollViewStyle={{paddingHorizontal: responsive.desktop ? 24 : 8}}
            />
          </React.Fragment>
        );
      }}
    </DataLoader>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
SettingsSalesStagesPageContent.query = gql`
  ${SettingsProjectStages.fragment}

  query SettingsSalesStagesPageContent {
    ${gql.query}
    viewer {
      id
      viewingOrganization {
        id
        companySettings {
          salesStages {
            id
            ...SettingsProjectStages
          }
          archivedSalesStages {
            id
            ...SettingsProjectStages
          }
        }
      }
    }
  }
`;

export default SettingsSalesStagesPageContent;
